import React, { useState, useEffect, useRef, useCallback } from 'react';
import { stockInwardAPI, storeAPI, itemAPI, unitAPI, vendorAPI, propertyAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowDownToLine, 
  Search, 
  Loader2, 
  Check, 
  ChevronRight,
  Star,
  Clock,
  Package,
  Store,
  Hash,
  AlertCircle,
  RotateCcw,
  Zap,
  Truck,
  Plus,
  X,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber, formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

// Quick Inward Steps
const STEPS = {
  SELECT_STORE: 1,
  ADD_ITEMS: 2,
  VENDOR_INFO: 3,
  CONFIRM: 4
};

export const QuickInwardPage = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_STORE);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  
  // Current item being added
  const [currentItem, setCurrentItem] = useState(null);
  const [currentQuantity, setCurrentQuantity] = useState('');
  const [currentRate, setCurrentRate] = useState('');
  const quantityInputRef = useRef(null);
  
  // Recent & Favorites
  const [recentItems, setRecentItems] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [lastUsedStore, setLastUsedStore] = useState(null);
  const [lastUsedVendor, setLastUsedVendor] = useState(null);

  useEffect(() => {
    fetchData();
    loadLocalPreferences();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, itemsRes, unitsRes, vendorsRes, propertiesRes] = await Promise.all([
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        vendorAPI.getAll({ status: 'active' }),
        propertyAPI.getAll({ status: 'active' })
      ]);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setVendors(vendorsRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLocalPreferences = () => {
    try {
      const savedRecent = localStorage.getItem('quickInward_recentItems');
      const savedFavorites = localStorage.getItem('quickEntry_favorites');
      const savedLastStore = localStorage.getItem('quickInward_lastStore');
      const savedLastVendor = localStorage.getItem('quickInward_lastVendor');
      
      if (savedRecent) setRecentItems(JSON.parse(savedRecent));
      if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
      if (savedLastStore) setLastUsedStore(JSON.parse(savedLastStore));
      if (savedLastVendor) setLastUsedVendor(JSON.parse(savedLastVendor));
    } catch (e) {
      console.error('Error loading preferences:', e);
    }
  };

  const saveRecentItem = (item) => {
    const updated = [item, ...recentItems.filter(i => i.id !== item.id)].slice(0, 10);
    setRecentItems(updated);
    localStorage.setItem('quickInward_recentItems', JSON.stringify(updated));
  };

  const saveLastStore = (store) => {
    setLastUsedStore(store);
    localStorage.setItem('quickInward_lastStore', JSON.stringify(store));
  };

  const saveLastVendor = (vendor) => {
    setLastUsedVendor(vendor);
    localStorage.setItem('quickInward_lastVendor', JSON.stringify(vendor));
  };

  const toggleFavorite = (item) => {
    const isFav = favorites.some(f => f.id === item.id);
    let updated;
    if (isFav) {
      updated = favorites.filter(f => f.id !== item.id);
    } else {
      updated = [...favorites, item];
    }
    setFavorites(updated);
    localStorage.setItem('quickEntry_favorites', JSON.stringify(updated));
    toast.success(isFav ? 'Removed from favorites' : 'Added to favorites');
  };

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const results = items.filter(item => 
      item.name.toLowerCase().includes(lowerQuery) ||
      item.code?.toLowerCase().includes(lowerQuery) ||
      item.category_name?.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);
    
    setSearchResults(results);
  }, [items]);

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    saveLastStore(store);
    setCurrentStep(STEPS.ADD_ITEMS);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleItemSelect = (item) => {
    setCurrentItem(item);
    saveRecentItem(item);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentQuantity('');
    setCurrentRate('');
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleAddItem = () => {
    const qty = parseFloat(currentQuantity);
    const rate = parseFloat(currentRate) || 0;
    
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    const newItem = {
      item_id: currentItem.id,
      item_name: currentItem.name,
      quantity: qty,
      unit_id: currentItem.default_unit_id,
      unit_name: currentItem.unit_name,
      rate: rate,
      amount: qty * rate
    };
    
    // Check if item already exists, update it
    const existingIndex = selectedItems.findIndex(i => i.item_id === currentItem.id);
    if (existingIndex >= 0) {
      const updated = [...selectedItems];
      updated[existingIndex] = newItem;
      setSelectedItems(updated);
    } else {
      setSelectedItems([...selectedItems, newItem]);
    }
    
    setCurrentItem(null);
    setCurrentQuantity('');
    setCurrentRate('');
    toast.success(`Added ${currentItem.name}`);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleRemoveItem = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const handleContinueToVendor = () => {
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    setCurrentStep(STEPS.VENDOR_INFO);
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    saveLastVendor(vendor);
  };

  const handleContinueToConfirm = () => {
    setCurrentStep(STEPS.CONFIRM);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const payload = {
        property_id: selectedStore.property_id,
        store_id: selectedStore.id,
        vendor_id: selectedVendor?.id || null,
        invoice_number: invoiceNumber || null,
        items: selectedItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          unit_id: item.unit_id,
          rate: item.rate,
          amount: item.amount
        }))
      };
      
      await stockInwardAPI.create(payload);
      
      const totalAmount = selectedItems.reduce((sum, item) => sum + item.amount, 0);
      const totalQty = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
      
      toast.success(
        <div className="space-y-1">
          <p className="font-semibold">Stock Inward Successful!</p>
          <p className="text-sm">Items: {selectedItems.length}</p>
          <p className="text-sm">Total Qty: {formatNumber(totalQty)}</p>
          <p className="text-sm">Total Amount: {formatCurrency(totalAmount)}</p>
          <p className="text-sm">Store: {selectedStore.name}</p>
        </div>,
        { duration: 5000 }
      );
      
      handleReset();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to record inward';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(STEPS.SELECT_STORE);
    setSelectedItems([]);
    setCurrentItem(null);
    setCurrentQuantity('');
    setCurrentRate('');
    setSelectedVendor(null);
    setInvoiceNumber('');
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleBack = () => {
    if (currentStep === STEPS.ADD_ITEMS) {
      setCurrentStep(STEPS.SELECT_STORE);
    } else if (currentStep === STEPS.VENDOR_INFO) {
      setCurrentStep(STEPS.ADD_ITEMS);
    } else if (currentStep === STEPS.CONFIRM) {
      setCurrentStep(STEPS.VENDOR_INFO);
    }
  };

  const totalAmount = selectedItems.reduce((sum, item) => sum + item.amount, 0);
  const totalQuantity = selectedItems.reduce((sum, item) => sum + item.quantity, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full">
          <ArrowDownToLine className="h-5 w-5 text-green-600" />
          <span className="font-semibold text-green-700">Quick Stock Inward</span>
        </div>
        <p className="text-muted-foreground">Receive stock quickly</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4].map((step) => (
          <React.Fragment key={step}>
            <div 
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all",
                currentStep >= step 
                  ? "bg-green-600 text-white" 
                  : "bg-muted text-muted-foreground"
              )}
            >
              {currentStep > step ? <Check className="h-5 w-5" /> : step}
            </div>
            {step < 4 && (
              <div className={cn(
                "w-12 h-1 rounded-full transition-all",
                currentStep > step ? "bg-green-600" : "bg-muted"
              )} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Labels */}
      <div className="flex justify-between text-xs text-muted-foreground px-2">
        <span className={currentStep >= 1 ? "text-green-600 font-medium" : ""}>Store</span>
        <span className={currentStep >= 2 ? "text-green-600 font-medium" : ""}>Items</span>
        <span className={currentStep >= 3 ? "text-green-600 font-medium" : ""}>Vendor</span>
        <span className={currentStep >= 4 ? "text-green-600 font-medium" : ""}>Confirm</span>
      </div>

      {/* Step 1: Select Store */}
      {currentStep === STEPS.SELECT_STORE && (
        <Card data-testid="quick-inward-store-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Select Receiving Store
            </CardTitle>
            <CardDescription>Where is the stock being received?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lastUsedStore && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Last Used</p>
                <Button
                  variant="outline"
                  className="w-full h-14 justify-start gap-3 border-green-500/50 bg-green-50"
                  onClick={() => handleStoreSelect(lastUsedStore)}
                  data-testid="last-used-store"
                >
                  <Clock className="h-5 w-5 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">{lastUsedStore.name}</p>
                    <p className="text-xs text-muted-foreground">{lastUsedStore.store_type || 'Store'}</p>
                  </div>
                </Button>
              </div>
            )}
            
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">All Stores</p>
              <div className="grid gap-2">
                {stores.map((store) => (
                  <Button
                    key={store.id}
                    variant="outline"
                    className="w-full h-12 justify-start gap-3"
                    onClick={() => handleStoreSelect(store)}
                    data-testid={`store-${store.id}`}
                  >
                    <Store className="h-4 w-4" />
                    <div className="text-left">
                      <p className="font-medium">{store.name}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Add Items */}
      {currentStep === STEPS.ADD_ITEMS && (
        <Card data-testid="quick-inward-items-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Add Items
            </CardTitle>
            <CardDescription>
              Receiving at <span className="font-medium text-foreground">{selectedStore?.name}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Added Items List */}
            {selectedItems.length > 0 && (
              <div className="space-y-2 p-3 rounded-lg bg-green-50 border border-green-200">
                <p className="text-xs font-medium text-green-700 uppercase tracking-wide">
                  Items Added ({selectedItems.length})
                </p>
                <div className="space-y-2">
                  {selectedItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <p className="font-medium text-sm">{item.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatNumber(item.quantity)} {item.unit_name} × {formatCurrency(item.rate)} = {formatCurrency(item.amount)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-medium">Total</span>
                    <span className="font-bold text-green-700">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Item Entry Form */}
            {currentItem ? (
              <div className="space-y-4 p-4 rounded-lg border-2 border-dashed border-green-300 bg-green-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{currentItem.name}</p>
                    <p className="text-xs text-muted-foreground">{currentItem.category_name}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentItem(null)}>
                    Cancel
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Quantity</label>
                    <Input
                      ref={quantityInputRef}
                      type="number"
                      placeholder="Qty"
                      value={currentQuantity}
                      onChange={(e) => setCurrentQuantity(e.target.value)}
                      className="h-12 text-lg"
                      min="0"
                      step="0.01"
                      data-testid="item-quantity-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Rate (₹)</label>
                    <Input
                      type="number"
                      placeholder="Rate"
                      value={currentRate}
                      onChange={(e) => setCurrentRate(e.target.value)}
                      className="h-12 text-lg"
                      min="0"
                      step="0.01"
                      data-testid="item-rate-input"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Amount: <span className="font-semibold text-foreground">{formatCurrency((parseFloat(currentQuantity) || 0) * (parseFloat(currentRate) || 0))}</span>
                  </span>
                  <Button onClick={handleAddItem} className="bg-green-600 hover:bg-green-700" data-testid="add-item-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search items to add..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10 h-12 text-lg"
                    autoFocus
                    data-testid="item-search-input"
                  />
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {searchResults.map((item) => {
                      const isFav = favorites.some(f => f.id === item.id);
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => handleItemSelect(item)}
                        >
                          <Package className="h-5 w-5 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.category_name || 'No category'} • {item.unit_name || 'Unit'}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(item); }}
                          >
                            <Star className={cn("h-4 w-4", isFav && "fill-yellow-400 text-yellow-400")} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Favorites and Recent */}
                {!searchQuery && (
                  <>
                    {favorites.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1">
                          <Star className="h-3 w-3" /> Favorites
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {favorites.slice(0, 6).map((item) => (
                            <Button
                              key={item.id}
                              variant="outline"
                              size="sm"
                              className="h-9"
                              onClick={() => handleItemSelect(item)}
                            >
                              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 mr-1" />
                              {item.name}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {recentItems.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1">
                          <Clock className="h-3 w-3" /> Recent
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {recentItems.slice(0, 6).map((item) => (
                            <Button
                              key={item.id}
                              variant="outline"
                              size="sm"
                              className="h-9"
                              onClick={() => handleItemSelect(item)}
                            >
                              {item.name}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Back
              </Button>
              <Button 
                onClick={handleContinueToVendor} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={selectedItems.length === 0}
                data-testid="continue-to-vendor"
              >
                Continue ({selectedItems.length} items) →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Vendor Info */}
      {currentStep === STEPS.VENDOR_INFO && (
        <Card data-testid="quick-inward-vendor-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Vendor & Invoice (Optional)
            </CardTitle>
            <CardDescription>Select vendor and enter invoice details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Last Used Vendor */}
            {lastUsedVendor && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Last Used</p>
                <Button
                  variant={selectedVendor?.id === lastUsedVendor.id ? "default" : "outline"}
                  className="w-full h-12 justify-start gap-3"
                  onClick={() => handleVendorSelect(lastUsedVendor)}
                >
                  <Clock className="h-4 w-4" />
                  {lastUsedVendor.name}
                </Button>
              </div>
            )}
            
            {/* Vendor Selection */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Select Vendor</p>
              <div className="grid gap-2 max-h-48 overflow-y-auto">
                <Button
                  variant={!selectedVendor ? "default" : "outline"}
                  className="w-full h-10 justify-start"
                  onClick={() => setSelectedVendor(null)}
                >
                  No Vendor / Direct Purchase
                </Button>
                {vendors.map((vendor) => (
                  <Button
                    key={vendor.id}
                    variant={selectedVendor?.id === vendor.id ? "default" : "outline"}
                    className="w-full h-10 justify-start gap-2"
                    onClick={() => handleVendorSelect(vendor)}
                    data-testid={`vendor-${vendor.id}`}
                  >
                    <Truck className="h-4 w-4" />
                    {vendor.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Invoice Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Invoice Number (Optional)</label>
              <Input
                placeholder="Enter invoice number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="h-12"
                data-testid="invoice-number-input"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Back
              </Button>
              <Button 
                onClick={handleContinueToConfirm} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="continue-to-confirm"
              >
                Review & Confirm →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Confirm */}
      {currentStep === STEPS.CONFIRM && (
        <Card data-testid="quick-inward-confirm-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              Confirm Inward
            </CardTitle>
            <CardDescription>Review and confirm stock receipt</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="p-6 rounded-xl bg-gradient-to-br from-green-100 to-green-50 border border-green-200 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Receiving Store</span>
                <span className="font-semibold">{selectedStore?.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Vendor</span>
                <span className="font-medium">{selectedVendor?.name || 'Direct Purchase'}</span>
              </div>
              {invoiceNumber && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-mono">{invoiceNumber}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Items</span>
                <span className="font-semibold">{selectedItems.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Quantity</span>
                <span className="font-semibold">{formatNumber(totalQuantity)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-green-200">
                <span className="text-muted-foreground">Total Amount</span>
                <span className="font-bold text-2xl text-green-700">{formatCurrency(totalAmount)}</span>
              </div>
            </div>

            {/* Items List */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Items</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {selectedItems.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-2 rounded bg-muted/50">
                    <span className="font-medium">{item.item_name}</span>
                    <span className="text-sm">
                      {formatNumber(item.quantity)} × {formatCurrency(item.rate)} = <span className="font-semibold">{formatCurrency(item.amount)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Edit
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="flex-1 h-12 text-lg bg-green-600 hover:bg-green-700"
                disabled={submitting}
                data-testid="confirm-inward-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ArrowDownToLine className="h-5 w-5 mr-2" />
                )}
                Receive Stock
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Footer */}
      <div className="flex justify-center gap-4 text-sm">
        <Button variant="ghost" size="sm" onClick={handleReset} data-testid="start-over-btn">
          <RotateCcw className="h-4 w-4 mr-1" />
          Start Over
        </Button>
      </div>
    </div>
  );
};

export default QuickInwardPage;
